import React, { useEffect, useState } from 'react';

import { Button, Checkbox, FieldText, YatSprite } from '@pkg/components';
import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { useSetYatName } from '@pkg/hooks';

import { NftMintSteps } from '@app/client';
import { checkYatLength } from '@app/helpers';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import { Text, Title } from '../YatMintFlow.style';

import {
    NameYatInputWrapper,
    StepContentWrapper,
    TermsCheckbox,
    TermsText,
    YatPrefix,
    YatPrefixSprite,
} from './MintingSteps.style';

interface NameYatProps {
    currentStep: NftMintSteps;
    onUpdate?: () => void;
}

export function NameYat({ currentStep, onUpdate }: NameYatProps) {
    const { addToast } = useToastStore();
    const { currentEmoji, visualizerName, setVisualizerName, visualizerNameIsLocked } = useEmojisStore();
    const { mutateAsync: updateName, isPending } = useSetYatName();

    const [yatName, setYatName] = useState<string | undefined>(visualizerName || undefined);
    const [isRename, setIsRename] = useState(false);
    const [checked, setChecked] = useState(false);

    useEffect(() => setIsRename(currentStep === NftMintSteps.RenameNft), [currentStep]);

    function handleUpdate() {
        if (yatName && yatName.length >= 2) {
            updateName(
                { name: yatName, eid: currentEmoji },
                {
                    onSuccess: (res) => {
                        if (res?.ok && res.value) {
                            setVisualizerName(yatName);

                            if (!isRename) {
                                onUpdate?.();
                            } else {
                                addToast({ message: 'Your NFT name has been updated!' });
                            }
                        }
                    },
                },
            );
        }
    }

    if (!currentEmoji) return null;

    const yatPrefix = (
        <YatPrefix>
            <YatPrefixSprite>
                <YatSprite emojiID={currentEmoji} />
            </YatPrefixSprite>
            <Text>{`|`}</Text>
        </YatPrefix>
    );

    const handleToggle = () => setChecked((checked) => !checked);

    const checkboxMarkup = !isRename ? (
        <TermsCheckbox onClick={handleToggle}>
            <Checkbox onChange={handleToggle} checked={checked} id="minting-acknowledgment" />
            <TermsText>
                I understand that by minting this Yat as an NFT, it can not be undone and I will no longer be able to
                transfer it to other users via the Yat Dashboard (this website). I also understand that if my gas fee is
                set too low, my transaction could be delayed or even fail resulting in a loss of the gas fee.
            </TermsText>
        </TermsCheckbox>
    ) : null;

    const ctaText = isRename ? 'Rename' : 'Mint my Yat';

    return (
        <StepContentWrapper key="naming">
            {!isRename ? (
                <>
                    <Title>What would you like to change your Yat name to?</Title>
                    <Text>This cannot be edited after you mint your Yat so choose wisely 😉</Text>
                </>
            ) : (
                <Text>This will update your Yat NFT metadata, so choose wisely 😉</Text>
            )}

            <NameYatInputWrapper>
                <FieldText
                    value={yatName}
                    inputMode="text"
                    prefix={yatPrefix}
                    prefixOffset={checkYatLength(currentEmoji) > 3 ? 100 : 70}
                    disabled={!!visualizerNameIsLocked}
                    placeholder="Name your Yat"
                    onChange={(value) => setYatName(value?.toString() || '')}
                />
            </NameYatInputWrapper>

            {checkboxMarkup}

            <Button
                disabled={!yatName || yatName.length < 2 || (!isRename && !checked)}
                loading={isPending}
                onClick={() => handleUpdate()}
            >
                {ctaText}
            </Button>
        </StepContentWrapper>
    );
}
